import React, { useEffect, useState } from 'react';
import { packagesApi, channelsApi } from '../lib/api';
import { Plus, Package, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const empty = { name: '', description: '', channel_ids: [], is_active: true };

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [p, c] = await Promise.all([packagesApi.getAll(), channelsApi.getAll()]);
      setPackages(p.data || []);
      setChannels(c.data || []);
    } catch {
      toast.error('Ошибка загрузки пакетов');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleChannel = (id) => {
    setForm((f) => {
      const has = f.channel_ids.includes(id);
      return {
        ...f,
        channel_ids: has ? f.channel_ids.filter((x) => x !== id) : [...f.channel_ids, id],
      };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.channel_ids.length === 0) {
      toast.error('Укажите название и каналы');
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await packagesApi.update(editing.id, form);
        toast.success('Пакет обновлён');
      } else {
        await packagesApi.create(form);
        toast.success('Пакет создан');
      }
      setOpen(false);
      setEditing(null);
      setForm(empty);
      load();
    } catch {
      toast.error('Ошибка сохранения');
    } finally {
      setSubmitting(false);
    }
  };

  const channelName = (id) => channels.find((c) => c.id === id)?.name || id.slice(0, 8);

  if (loading) return <div className="text-zinc-400">Загрузка…</div>;

  return (
    <div data-testid="packages-page">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-violet-500/10 flex items-center justify-center">
            <Package className="w-6 h-6 text-violet-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Пакеты</h1>
            <p className="text-sm text-zinc-500">Несколько каналов в одном тарифе</p>
          </div>
        </div>
        <Button
          onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
          className="bg-[#2AABEE] hover:bg-[#2AABEE]/90"
          data-testid="package-add-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Добавить пакет
        </Button>
      </div>

      <div className="space-y-3">
        {packages.length === 0 && (
          <p className="text-zinc-500">Пакетов нет. Создайте или импортируйте из Emergent.</p>
        )}
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            className="p-4 rounded-xl border border-white/5 bg-[#0a0a0a] flex items-start justify-between gap-4"
            data-testid={`package-row-${pkg.id}`}
          >
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-white">{pkg.name}</h3>
                <span className={`text-xs px-2 py-0.5 rounded ${pkg.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}`}>
                  {pkg.is_active ? 'Активен' : 'Выкл'}
                </span>
              </div>
              {pkg.description && <p className="text-sm text-zinc-500 mt-1">{pkg.description}</p>}
              <p className="text-sm text-zinc-400 mt-2">
                Каналы: {(pkg.channel_ids || []).map(channelName).join(', ') || '—'}
              </p>
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setEditing(pkg);
                  setForm({
                    name: pkg.name || '',
                    description: pkg.description || '',
                    channel_ids: pkg.channel_ids || [],
                    is_active: pkg.is_active !== false,
                  });
                  setOpen(true);
                }}
              >
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  if (!window.confirm('Удалить пакет?')) return;
                  try {
                    await packagesApi.delete(pkg.id);
                    toast.success('Удалено');
                    load();
                  } catch {
                    toast.error('Ошибка удаления');
                  }
                }}
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Редактировать пакет' : 'Новый пакет'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Название</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#121212] border-zinc-800"
                data-testid="package-name-input"
              />
            </div>
            <div>
              <Label>Описание</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-[#121212] border-zinc-800"
              />
            </div>
            <div>
              <Label className="mb-2 block">Каналы</Label>
              <div className="space-y-2 max-h-48 overflow-auto">
                {channels.map((ch) => (
                  <label key={ch.id} className="flex items-center gap-2 text-sm text-zinc-300">
                    <input
                      type="checkbox"
                      checked={form.channel_ids.includes(ch.id)}
                      onChange={() => toggleChannel(ch.id)}
                    />
                    {ch.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
              <Label>Активен</Label>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={submitting} className="bg-[#2AABEE]">
                {submitting ? '…' : 'Сохранить'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
